import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FileUploadPanel } from '@/components/common/form-inputs/FileUploadPanel';
import { FormInput } from '@/components/common/form-inputs/FormInput';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { UploadedDocument, uploadToStorage } from '@/helpers/storage.helper';
import { toast } from '@/hooks/ui/use-toast';
import { useCreateZeeDocument } from '@/hooks/zee-document/useCreateZeeDocument';
import { useZeeDocumentStore } from '@/stores/useZeeDocumentStore';
import { StageItem } from '@/types/stage.item.type';
import { Stage } from '@/types/stage.type';

import { UploadZeeDocumentFormSchema } from '../forms/schemas/UploadZeeDocumentFormSchema';

interface UploadZeeDocumentDialogProps {
  open: boolean;
  stage: Stage;
  stageItem: StageItem;
  setOpen: (open: boolean) => void;
}

const UploadZeeDocumentDialog = ({
  open,
  stage,
  stageItem,
  setOpen,
}: UploadZeeDocumentDialogProps) => {
  const uploadForm = useForm<z.infer<typeof UploadZeeDocumentFormSchema>>({
    resolver: zodResolver(UploadZeeDocumentFormSchema),
  });

  const { zeeDocuments, setZeeDocuments } = useZeeDocumentStore();

  const [isUploading, setIsUploading] = useState(false);

  const {
    mutate: createZeeDocument,
    result: createZeeDocumentResult,
    isError: isCreateZeeDocumentError,
    isSuccess: isCreateZeeDocumentSuccess,
    isLoading: isCreateZeeDocumentLoading,
  } = useCreateZeeDocument();

  const uploadZeeDocument = async (
    file: File
  ): Promise<UploadedDocument | null> => {
    setIsUploading(true);

    try {
      const uploaded = await uploadToStorage(file, 'documents');

      toast({
        title: 'Document uploaded successfully',
        variant: 'default',
      });

      return uploaded;
    } catch (error) {
      console.error(error);

      toast({
        title: 'Something went wrong',
        description:
          error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });

      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (
    values: z.infer<typeof UploadZeeDocumentFormSchema>
  ): Promise<void> => {
    try {
      const uploadedDocument = await uploadZeeDocument(values.file);

      if (!uploadedDocument) {
        return;
      }

      createZeeDocument({
        file_name: values.file_name,
        file_url: uploadedDocument.file_url,
        file_size: uploadedDocument.file_size,
        stage_name: stage.name || '',
        stage_item_title: stageItem.title || '',
        stage_item_description: stageItem.description || '',
      });
    } catch (error) {
      console.error(error);

      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    uploadForm.reset();
    setOpen(false);
  };

  useEffect(() => {
    if (isCreateZeeDocumentError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isCreateZeeDocumentError]);

  useEffect(() => {
    if (isCreateZeeDocumentSuccess && createZeeDocumentResult?.data) {
      setZeeDocuments([...(zeeDocuments || []), createZeeDocumentResult.data]);
      uploadForm.reset();
      setOpen(false);
    }
  }, [createZeeDocumentResult, isCreateZeeDocumentSuccess]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) uploadForm.reset();
      }}
    >
      <DialogContent
        className="w-[480px] bg-white"
        aria-describedby="undefined"
      >
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>
        <Form {...uploadForm}>
          <form
            onSubmit={uploadForm.handleSubmit(handleSubmit)}
            className="space-y-2"
          >
            <FileUploadPanel name="file" control={uploadForm.control} />
            <FormInput
              type="text"
              name="file_name"
              label="Title"
              control={uploadForm.control}
            />

            <DialogFooter>
              <div className="flex w-full justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isUploading || isCreateZeeDocumentLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isUploading || isCreateZeeDocumentLoading}
                >
                  {(isUploading || isCreateZeeDocumentLoading) && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  Upload
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export { UploadZeeDocumentDialog };
